'use client'

import { useEffect, useRef } from 'react'
import Image from 'next/image'

/** The baked map's size (src/content/world-map.json), for the box's aspect ratio before the scene loads. */
export const WORLD_MAP_SIZE = { width: 1200, height: 604 }

/**
 * World map for the service-page International Experience section: the
 * static map image, replaced by the Three.js dot-matrix scene
 * (world-map-scene.ts) once it loads after hydration. The scene stays out of
 * the first bundle; without WebGL the static image shows.
 */
export function WorldMap({ fallbackSrc, title }: { fallbackSrc: string; title: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const image = imageRef.current
    if (!canvas || !image) return
    let cancelled = false
    let cleanup: (() => void) | undefined
    import('./world-map-scene')
      .then(({ mountWorldMap }) => {
        if (!cancelled) cleanup = mountWorldMap(canvas, image)
      })
      .catch(() => {
        if (!cancelled) image.style.visibility = 'visible' // the scene didn't load — show the static map
      })
    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [])

  return (
    <div
      className="world-map"
      role="img"
      aria-label={title}
      style={{ aspectRatio: `${WORLD_MAP_SIZE.width} / ${WORLD_MAP_SIZE.height}` }}
    >
      <Image
        ref={imageRef}
        src={fallbackSrc}
        alt=""
        width={1400}
        height={922}
        className="world-map-fallback"
        style={{ visibility: 'hidden' }}
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={fallbackSrc} alt="" className="world-map-fallback" />
      </noscript>
      <canvas ref={canvasRef} aria-hidden="true" />
    </div>
  )
}
